export interface GoogleAddress {
  formattedAddress: string;
  addressComponents: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    country: IsoCountryCode | "";
    postalCode: string;
  };
  location: {
    lat: number;
    lng: number;
  };
  raw: google.maps.places.PlaceResult; // Keep the raw Google result for flexibility
}

// AutocompleteAddress.tsx
import { useEffect, useState, useCallback, useRef, useId } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { Button, Input } from "@heroui/react";
import {
  Select as UiSelect,
  SelectContent as UiSelectContent,
  SelectItem as UiSelectItem,
  SelectTrigger as UiSelectTrigger,
  SelectValue as UiSelectValue,
} from "@/components/ui/select";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import { ADDRESS_LOOKUP_LANGUAGE } from "@/services/address-lookup-language";
import { logger } from "@/services/logger";
import { useTranslation } from "react-i18next";
import { countryCodeForValue, getCountryOptions } from "@/lib/countries";
import type { IsoCountryCode } from "@/lib/countries";

interface Props {
  onSelectedAddress: (address: GoogleAddress | null) => void;
  placeholder?: string;
  initialValue?: string;
  initialAddressComponents?: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
  };
  showEditFormByDefault?: boolean;
  readOnly?: boolean;
  showFullForm?: boolean;
  onlyShowAutocomplete?: boolean;
}

interface EditableAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  country: IsoCountryCode | "";
  postalCode: string;
}

// Module-scope pure helper so useEffect's dependency array doesn't need
// to list a render-local function reference.
function getFormattedAddress(components: EditableAddress): string {
  const { line1, line2, city, state, country, postalCode } = components;

  let cityProvince = "";
  if (city && state) cityProvince = `${city}, ${state}`;
  else if (city) cityProvince = city;
  else if (state) cityProvince = state;

  const cityProvPostal = postalCode
    ? `${cityProvince} ${postalCode}`
    : cityProvince;

  const parts = [line1, line2, cityProvPostal, country || "CA"].filter(Boolean);
  return parts.join(", ");
}

export function withAddressComponents(
  place: GoogleAddress,
  patch: Partial<GoogleAddress["addressComponents"]>
): GoogleAddress {
  return {
    ...place,
    addressComponents: { ...place.addressComponents, ...patch },
  };
}

export function AutocompleteAddress({
  onSelectedAddress,
  placeholder,
  initialValue,
  initialAddressComponents,
  showEditFormByDefault = false,
  readOnly = false,
  showFullForm = false,
  onlyShowAutocomplete = false,
}: Props) {
  const { t, i18n } = useTranslation("app");
  const countryOptions = getCountryOptions(i18n.language);
  const places = useMapsLibrary("places");
  const fieldIdPrefix = useId();

  const [isOpen, setIsOpen] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedPlace, setSelectedPlace] = useState<GoogleAddress | null>(
    null
  );
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);

  // Services
  const [sessionToken, setSessionToken] =
    useState<google.maps.places.AutocompleteSessionToken>();
  const [autocompleteService, setAutocompleteService] =
    useState<google.maps.places.AutocompleteService | null>(null);
  const [placesService, setPlacesService] =
    useState<google.maps.places.PlacesService | null>(null);

  // Form state
  const [editableAddress, setEditableAddress] = useState<EditableAddress>({
    line1: "",
    line2: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
  });

  // Add a ref to track if we're interacting with the edit form
  const editFormRef = useRef<HTMLDivElement>(null);

  // Add state to track if we're editing
  const [isEditing, setIsEditing] = useState(false);

  // Add postal code formatting function
  const formatPostalCode = (value: string): string => {
    // Remove all non-alphanumeric characters and convert to uppercase
    const cleaned = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

    // Canadian postal code format: A1A 1A1
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
    } else {
      // Limit to 6 characters max
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)}`;
    }
  };

  // Handle postal code change with formatting
  const handlePostalCodeChange = (value: string) => {
    const formatted = formatPostalCode(value);
    setEditableAddress((prev) => ({
      ...prev,
      postalCode: formatted,
    }));
    if (selectedPlace) {
      const nextPlace = withAddressComponents(selectedPlace, {
        postalCode: formatted,
      });
      setSelectedPlace(nextPlace);
      onSelectedAddress(nextPlace);
    }
  };

  // Initialize Google services. Defer setState so it's not synchronous within
  // the effect body.
  useEffect(() => {
    if (!places) return;

    try {
      const autocomplete = new places.AutocompleteService();
      const placesServ = new places.PlacesService(
        document.createElement("div")
      );
      const token = new places.AutocompleteSessionToken();

      void Promise.resolve().then(() => {
        setAutocompleteService(autocomplete);
        setPlacesService(placesServ);
        setSessionToken(token);
      });
    } catch (error) {
      logger.error("Error initializing services:", error);
    }
  }, [places]);

  // Handle predictions fetch
  const fetchPredictions = useCallback(
    async (input: string) => {
      logger.info("Fetching predictions for:", !input, !autocompleteService);
      if (!autocompleteService || !input) {
        setPredictions([]);
        return;
      }

      try {
        const response = await autocompleteService.getPlacePredictions({
          input,
          sessionToken,
          componentRestrictions: { country: "ca" },
          region: "CA",
          language: ADDRESS_LOOKUP_LANGUAGE,
        });

        // Add debug logging
        logger.info("Predictions response:", response);

        setPredictions(response?.predictions || []);
      } catch (error) {
        logger.error("Error fetching predictions:", error);
        setPredictions([]);
      }
    },
    [autocompleteService, sessionToken]
  );

  // Handle place selection
  const handlePlaceSelect = useCallback(
    async (placeId: string) => {
      if (!placesService || !places) return;

      try {
        await new Promise((resolve, reject) => {
          placesService.getDetails(
            {
              placeId,
              fields: ["address_components", "geometry", "formatted_address"],
              sessionToken,
              language: ADDRESS_LOOKUP_LANGUAGE,
              region: "CA",
            },
            (result, status) => {
              if (
                status === google.maps.places.PlacesServiceStatus.OK &&
                result
              ) {
                // Check for subpremise (unit/apartment number) in address components
                const subpremise = result.address_components?.find(
                  (component) => component.types.includes("subpremise")
                )?.long_name;

                const address: GoogleAddress = {
                  formattedAddress: result.formatted_address ?? "",
                  addressComponents: {
                    line1: "",
                    line2: subpremise ?? "",
                    city: "",
                    state: "",
                    country: "",
                    postalCode: "",
                  },
                  location: {
                    lat: result.geometry?.location?.lat() ?? 0,
                    lng: result.geometry?.location?.lng() ?? 0,
                  },
                  raw: result,
                };

                // Rest of the address parsing...
                const streetNumber =
                  result.address_components?.find((component) =>
                    component.types.includes("street_number")
                  )?.long_name ?? "";

                const route =
                  result.address_components?.find((component) =>
                    component.types.includes("route")
                  )?.long_name ?? "";

                address.addressComponents.line1 =
                  `${streetNumber} ${route}`.trim();

                // Parse other components as before...
                result.address_components?.forEach((component) => {
                  const types = component.types;
                  if (types.includes("locality")) {
                    address.addressComponents.city = component.long_name;
                  }
                  if (types.includes("administrative_area_level_1")) {
                    // Use short_name for province code (ON, BC, etc.) as server expects codes
                    address.addressComponents.state = component.short_name;
                  }
                  if (types.includes("country")) {
                    address.addressComponents.country =
                      countryCodeForValue(component.short_name) ?? "";
                  }
                  if (types.includes("postal_code")) {
                    address.addressComponents.postalCode = component.long_name;
                  }
                });

                setSelectedPlace(address);
                setEditableAddress(address.addressComponents);
                setShowEditForm(true);
                setIsEditing(true);
                onSelectedAddress(address);
                setSessionToken(new places.AutocompleteSessionToken());
                resolve(address);
              } else {
                reject(new Error(`Place details failed: ${status}`));
              }
            }
          );
        });
      } catch (error) {
        logger.error("Error fetching place details:", error);
      }
    },
    [onSelectedAddress, places, placesService, sessionToken]
  );

  const handleReset = useCallback(() => {
    setSelectedPlace(null);
    setShowEditForm(false);
    setInputValue("");
    setEditableAddress({
      line1: "",
      line2: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
    });
    onSelectedAddress(null);
  }, [onSelectedAddress]);

  // Sync controlled initialValue into internal inputValue state. Defer to
  // avoid setState-in-effect warning.
  useEffect(() => {
    if (!initialValue) return;
    void Promise.resolve().then(() => setInputValue(initialValue));
  }, [initialValue]);

  // Seed edit form state from provided initial address components. Defer to
  // avoid setState-in-effect warning.
  useEffect(() => {
    if (!initialAddressComponents) return;
    const canonicalComponents: EditableAddress = {
      ...initialAddressComponents,
      country: countryCodeForValue(initialAddressComponents.country) ?? "",
    };
    const formatted = initialValue ?? getFormattedAddress(canonicalComponents);
    void Promise.resolve().then(() => {
      setEditableAddress(canonicalComponents);
      setShowEditForm(showEditFormByDefault);
      setSelectedPlace({
        formattedAddress: formatted,
        addressComponents: canonicalComponents,
        location: { lat: 0, lng: 0 },
        raw: {} as google.maps.places.PlaceResult,
      });
    });
  }, [initialAddressComponents, initialValue, showEditFormByDefault]);

  const selectedCountryCode = countryCodeForValue(editableAddress.country);

  return (
    <div className="space-y-4">
      {selectedPlace ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Input value={getFormattedAddress(editableAddress)} readOnly />
            {!readOnly && (
              <>
                {!onlyShowAutocomplete && (
                  <Button
                    type="button"
                    isIconOnly
                    variant="bordered"
                    onClick={() => {
                      setShowEditForm(!showEditForm);
                      setIsEditing(!isEditing);
                    }}
                  >
                    {showEditForm ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                )}
                <Button
                  type="button"
                  isIconOnly
                  variant="bordered"
                  onClick={() => {
                    handleReset();
                    setIsEditing(false);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>

          {(showFullForm || (showEditForm && !readOnly)) &&
            !onlyShowAutocomplete && (
              <div className="space-y-4 border rounded-lg p-4 bg-background">
                <div className="space-y-4">
                  {!readOnly && (
                    <div className="text-sm text-default-500 bg-primary/5 p-3 rounded-lg border border-primary/20">
                      <p className="font-medium text-foreground mb-1">
                        {t("addressAutocomplete.editingTitle")}
                      </p>
                      <p className="text-default-700">
                        {t("addressAutocomplete.editingBody")}
                      </p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <label
                      className="text-sm font-medium text-default-700"
                      htmlFor={`${fieldIdPrefix}-line2`}
                    >
                      {t("addressAutocomplete.line2")}
                    </label>
                    <Input
                      id={`${fieldIdPrefix}-line2`}
                      value={editableAddress.line2}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setEditableAddress((prev) => ({
                          ...prev,
                          line2: newValue,
                        }));
                        if (selectedPlace) {
                          onSelectedAddress({
                            ...selectedPlace,
                            addressComponents: {
                              ...selectedPlace.addressComponents,
                              line2: newValue,
                            },
                          });
                        }
                      }}
                      placeholder={t("addressAutocomplete.line2Placeholder")}
                      disabled={readOnly}
                    />
                  </div>

                  <div className="space-y-2">
                    <label
                      className="text-sm font-medium text-default-700"
                      htmlFor={`${fieldIdPrefix}-line1`}
                    >
                      {t("addressAutocomplete.line1")}
                    </label>
                    <Input
                      id={`${fieldIdPrefix}-line1`}
                      value={editableAddress.line1}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setEditableAddress((prev) => ({
                          ...prev,
                          line1: newValue,
                        }));
                        if (selectedPlace) {
                          onSelectedAddress({
                            ...selectedPlace,
                            addressComponents: {
                              ...selectedPlace.addressComponents,
                              line1: newValue,
                            },
                          });
                        }
                      }}
                      placeholder={t("addressAutocomplete.line1Placeholder")}
                      disabled={
                        readOnly ||
                        !!(
                          editableAddress.line1 &&
                          editableAddress.line1.trim() !== ""
                        )
                      }
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label
                        className="text-sm font-medium text-default-700"
                        htmlFor={`${fieldIdPrefix}-city`}
                      >
                        {t("addressAutocomplete.city")}
                      </label>
                      <Input
                        id={`${fieldIdPrefix}-city`}
                        value={editableAddress.city}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setEditableAddress((prev) => ({
                            ...prev,
                            city: newValue,
                          }));
                          if (selectedPlace) {
                            onSelectedAddress({
                              ...selectedPlace,
                              addressComponents: {
                                ...selectedPlace.addressComponents,
                                city: newValue,
                              },
                            });
                          }
                        }}
                        placeholder={t("addressAutocomplete.cityPlaceholder")}
                        disabled={
                          readOnly ||
                          !!(
                            editableAddress.city &&
                            editableAddress.city.trim() !== ""
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <label
                        className="text-sm font-medium text-default-700"
                        htmlFor={`${fieldIdPrefix}-state`}
                      >
                        {t("addressAutocomplete.state")}
                      </label>
                      <Input
                        id={`${fieldIdPrefix}-state`}
                        value={editableAddress.state}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setEditableAddress((prev) => ({
                            ...prev,
                            state: newValue,
                          }));
                          if (selectedPlace) {
                            onSelectedAddress({
                              ...selectedPlace,
                              addressComponents: {
                                ...selectedPlace.addressComponents,
                                state: newValue,
                              },
                            });
                          }
                        }}
                        placeholder={t("addressAutocomplete.statePlaceholder")}
                        disabled={
                          readOnly ||
                          !!(
                            editableAddress.state &&
                            editableAddress.state.trim() !== ""
                          )
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label
                        className="text-sm font-medium text-default-700"
                        htmlFor={`${fieldIdPrefix}-postal-code`}
                      >
                        {t("addressAutocomplete.postalCode")}
                      </label>
                      <Input
                        id={`${fieldIdPrefix}-postal-code`}
                        value={editableAddress.postalCode}
                        onChange={(e) => {
                          handlePostalCodeChange(e.target.value);
                        }}
                        placeholder="A1A 1A1"
                        disabled={readOnly}
                        maxLength={7} // A1A 1A1 = 7 characters including space
                        className={
                          !editableAddress.postalCode ||
                          editableAddress.postalCode.trim() === ""
                            ? "border-warning/40 bg-warning/10"
                            : ""
                        }
                      />
                      {(!editableAddress.postalCode ||
                        editableAddress.postalCode.trim() === "") && (
                        <p className="text-xs text-warning">
                          {t("addressAutocomplete.postalCodeWarning")}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label
                        className="text-sm font-medium text-default-700"
                        htmlFor={`${fieldIdPrefix}-country`}
                      >
                        {t("addressAutocomplete.country")}
                      </label>
                      <UiSelect
                        value={selectedCountryCode}
                        onValueChange={(value) => {
                          const newValue = countryCodeForValue(value) ?? "";
                          setEditableAddress((prev) => ({
                            ...prev,
                            country: newValue,
                          }));
                          if (selectedPlace) {
                            const nextPlace = withAddressComponents(
                              selectedPlace,
                              { country: newValue }
                            );
                            setSelectedPlace(nextPlace);
                            onSelectedAddress(nextPlace);
                          }
                        }}
                        disabled={readOnly}
                      >
                        <UiSelectTrigger
                          data-testid="country-select"
                          id={`${fieldIdPrefix}-country`}
                          aria-label={t("addressAutocomplete.country")}
                        >
                          <UiSelectValue
                            placeholder={t(
                              "addressAutocomplete.countryPlaceholder"
                            )}
                          />
                        </UiSelectTrigger>
                        <UiSelectContent>
                          {countryOptions.map((country) => (
                            <UiSelectItem
                              key={country.code}
                              value={country.code}
                            >
                              {country.label}
                            </UiSelectItem>
                          ))}
                        </UiSelectContent>
                      </UiSelect>
                    </div>
                  </div>
                </div>

                {!showFullForm && !readOnly && (
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="bordered"
                      onClick={() => {
                        setShowEditForm(false);
                        setIsEditing(false);
                      }}
                    >
                      {t("addressAutocomplete.close")}
                    </Button>
                  </div>
                )}
              </div>
            )}
        </div>
      ) : (
        <div className="relative">
          <Input
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              void fetchPredictions(e.target.value);
            }}
            onBlur={(e) => {
              if (!editFormRef.current?.contains(e.relatedTarget as Node)) {
                setTimeout(() => {
                  setPredictions([]);
                  setIsOpen(false);
                }, 200);
              }
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            disabled={readOnly}
          />
          {isOpen && predictions.length > 0 && !readOnly && (
            <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg">
              {predictions.map((prediction) => (
                <button
                  type="button"
                  key={prediction.place_id}
                  className="block w-full px-4 py-2 text-left hover:bg-muted cursor-pointer"
                  onClick={() => {
                    void handlePlaceSelect(prediction.place_id);
                    setIsOpen(false);
                    setPredictions([]);
                  }}
                >
                  {prediction.description}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
